import { html, nothing } from "lit";
import type { SessionEnvironmentExplainResult } from "../types.ts";
import { icons } from "../icons.ts";

export type SessionEnvironmentPanelProps = {
  loading: boolean;
  recreating: boolean;
  previewRegistering: boolean;
  previewPort: number;
  error: string | null;
  data: SessionEnvironmentExplainResult | null;
  onRefresh: () => void;
  onRecreate: () => void;
  onPreviewPortChange?: (port: number) => void;
  onRegisterPreview?: () => void;
  onClearPreview?: () => void;
  onScrollToWorkspace?: () => void;
};

export function environmentBadge(data: SessionEnvironmentExplainResult): string {
  if (!data.sandboxed) {
    return "Host";
  }
  if (data.scope === "session") {
    return "Sandbox (session)";
  }
  if (data.scope === "agent") {
    return "Sandbox (agent)";
  }
  return "Sandbox (shared)";
}

export function containerStatus(data: SessionEnvironmentExplainResult): string {
  if (!data.sandboxed) {
    return "No container — tools run on the gateway host";
  }
  if (!data.dockerReachable) {
    return "Docker unreachable from gateway";
  }
  if (!data.containerName) {
    return "Container not created yet (starts on first tool run)";
  }
  if (!data.containerExists) {
    return `${data.containerName} — not created yet`;
  }
  return data.containerRunning
    ? `${data.containerName} — running`
    : `${data.containerName} — stopped`;
}

export function renderSessionEnvironmentPanel(props: SessionEnvironmentPanelProps) {
  const data = props.data;

  return html`
    <div class="session-environment-panel">
      <div class="session-environment-panel__toolbar">
        <button
          type="button"
          class="btn subtle"
          ?disabled=${props.loading || props.recreating}
          @click=${props.onRefresh}
        >
          ${props.loading ? "Loading…" : "Refresh"}
        </button>
        <button
          type="button"
          class="btn"
          ?disabled=${props.loading || props.recreating || !data}
          @click=${props.onRecreate}
        >
          ${props.recreating ? `${icons.loader} Recreating…` : "Recreate environment"}
        </button>
        ${
          props.onScrollToWorkspace
            ? html`
                <button type="button" class="btn subtle" @click=${props.onScrollToWorkspace}>
                  Workspace files
                </button>
              `
            : nothing
        }
      </div>

      ${props.error ? html`<p class="callout danger">${props.error}</p>` : nothing}

      ${
        data
          ? html`
              <div class="session-environment-panel__grid">
                <div class="session-environment-panel__row">
                  <span class="muted">Runtime</span>
                  <span class="session-environment-panel__badge">${environmentBadge(data)}</span>
                </div>
                <div class="session-environment-panel__row">
                  <span class="muted">Container</span>
                  <span class="mono">${containerStatus(data)}</span>
                </div>
                <div class="session-environment-panel__row">
                  <span class="muted">Workspace</span>
                  <span class="mono" title=${data.workspaceDir}>${data.workspaceDir}</span>
                </div>
                <div class="session-environment-panel__row">
                  <span class="muted">Image</span>
                  <span class="mono">${data.image}</span>
                </div>
                <div class="session-environment-panel__row">
                  <span class="muted">Network</span>
                  <span>${data.network}</span>
                </div>
              </div>
              ${
                data.hostWarning
                  ? html`<p class="callout warn session-environment-panel__warn">${data.hostWarning}</p>`
                  : nothing
              }
              ${
                data.sandboxed && !data.dockerReachable
                  ? html`
                      <p class="callout warn">
                        Docker is not reachable. Mount docker.sock on the gateway host (see Help → session environments).
                      </p>
                    `
                  : nothing
              }
              ${
                data.sandboxed && data.containerRunning
                  ? html`
                      <div class="session-environment-panel__preview">
                        <div class="session-environment-panel__row">
                          <span class="muted">MVP preview</span>
                          <span>
                            ${
                              data.preview?.registered && data.preview.previewUrl
                                ? html`<a href=${data.preview.previewUrl} target="_blank" rel="noopener noreferrer">Open preview</a>`
                                : html`
                                    <span class="muted">Not registered</span>
                                  `
                            }
                          </span>
                        </div>
                        <div class="session-environment-panel__preview-actions">
                          <label class="session-environment-panel__port">
                            <span class="muted">Port</span>
                            <input
                              type="number"
                              min="3000"
                              max="9999"
                              .value=${String(props.previewPort)}
                              ?disabled=${props.previewRegistering || props.recreating}
                              @change=${(e: Event) => {
                                const value = Number((e.target as HTMLInputElement).value);
                                if (Number.isFinite(value)) {
                                  props.onPreviewPortChange?.(value);
                                }
                              }}
                            />
                          </label>
                          <button
                            type="button"
                            class="btn subtle"
                            ?disabled=${props.previewRegistering || props.loading}
                            @click=${props.onRegisterPreview}
                          >
                            ${props.previewRegistering ? "Registering…" : "Register preview"}
                          </button>
                          ${
                            data.preview?.registered
                              ? html`
                                  <button
                                    type="button"
                                    class="btn subtle"
                                    ?disabled=${props.previewRegistering}
                                    @click=${props.onClearPreview}
                                  >
                                    Clear
                                  </button>
                                  <button
                                    type="button"
                                    class="btn subtle"
                                    ?disabled=${!data.preview.previewUrl}
                                    @click=${async () => {
                                      if (data.preview?.previewUrl) {
                                        await navigator.clipboard.writeText(
                                          data.preview.previewUrl,
                                        );
                                      }
                                    }}
                                  >
                                    Copy URL
                                  </button>
                                `
                              : nothing
                          }
                        </div>
                        <p class="muted session-environment-panel__preview-hint">
                          Start your dev server on the port inside the sandbox (e.g. Vite 5173), then register.
                          Links expire after 24h.
                        </p>
                      </div>
                    `
                  : nothing
              }
            `
          : props.loading
            ? html`
                <p class="muted">Loading environment…</p>
              `
            : html`
                <p class="muted">Connect to the gateway to inspect this session environment.</p>
              `
      }
    </div>
  `;
}
